import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Mail, Smartphone } from "lucide-react";

export function SecurityDialog({ open, onOpenChange }) {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [pwPrompt, setPwPrompt] = useState(false);
  const [pw, setPw] = useState("");
  const [method, setMethod] = useState(user?.twofa_method || "email");
  const enabled = !!user?.twofa_enabled;
  const hasPhone = !!(user?.phone && String(user.phone).trim());

  useEffect(() => { if (open) { setMethod(user?.twofa_method || "email"); setPw(""); setPwPrompt(false); } }, [open, user]);

  const doEnable = async (m) => {
    setSaving(true);
    try {
      await api.post("/auth/2fa/enable", { method: m });
      await refreshUser();
      toast.success(m === "sms" ? "SMS ile iki adımlı doğrulama açıldı" : "E-posta ile iki adımlı doğrulama açıldı");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDisable = async (password) => {
    setSaving(true);
    try {
      await api.post("/auth/2fa/disable", { password });
      await refreshUser();
      setPwPrompt(false); setPw("");
      toast.success("İki adımlı doğrulama kapatıldı");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); setPw(""); }
  };

  const toggle = async (val) => {
    if (!val && enabled) { setPwPrompt(true); return; }  // kapatmak için şifre teyidi
    if (val) {
      if (method === "sms" && !hasPhone) { toast.error("SMS için hesabınızda kayıtlı telefon numarası yok"); return; }
      await doEnable(method);
    }
  };

  const changeMethod = async (m) => {
    setMethod(m);
    if (enabled) {  // zaten açıksa yöntemi anında değiştir
      if (m === "sms" && !hasPhone) { toast.error("SMS için hesabınızda kayıtlı telefon numarası yok"); return; }
      await doEnable(m);
    }
  };

  const close = (o) => { if (!o) { setPwPrompt(false); setPw(""); } onOpenChange(o); };
  const activeMethodLabel = (user?.twofa_method || "email") === "sms" ? "SMS" : "E-posta";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="security-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ShieldCheck className="h-5 w-5 text-brand-green" /> Güvenlik
          </DialogTitle>
          <DialogDescription>Hesap giriş güvenliğinizi yönetin.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2" data-testid="twofa-method-group">
          <p className="text-sm font-semibold text-slate-700">Doğrulama Yöntemi</p>
          <label data-testid="twofa-method-email" className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${method === "email" ? "border-brand-green bg-brand-green/5" : "border-slate-200 hover:border-slate-300"}`}>
            <input type="radio" name="twofa-method" checked={method === "email"} onChange={() => changeMethod("email")} className="h-4 w-4 text-brand-green" />
            <Mail className="h-4 w-4 text-slate-400" />
            <div className="min-w-0"><p className="text-sm font-medium text-slate-800">E-posta</p><p className="truncate text-[11px] text-slate-500">{user?.email}</p></div>
          </label>
          <label data-testid="twofa-method-sms" className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${method === "sms" ? "border-brand-green bg-brand-green/5" : "border-slate-200 hover:border-slate-300"} ${hasPhone ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}>
            <input type="radio" name="twofa-method" checked={method === "sms"} disabled={!hasPhone} onChange={() => changeMethod("sms")} className="h-4 w-4 text-brand-green" />
            <Smartphone className="h-4 w-4 text-slate-400" />
            <div className="min-w-0"><p className="text-sm font-medium text-slate-800">SMS</p><p className="truncate text-[11px] text-slate-500">{hasPhone ? user.phone : "Hesabınızda telefon numarası yok"}</p></div>
          </label>
        </div>

        <div className="flex items-start justify-between gap-4 rounded-lg bg-slate-50 p-4 ring-1 ring-inset ring-slate-200">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800">İki Adımlı Doğrulama</p>
            <p className="mt-1 text-xs text-slate-500">
              Açıldığında her girişte {method === "sms" ? "telefonunuza SMS ile" : "e-postanıza"} 6 haneli kod gönderilir.
            </p>
          </div>
          <div className="flex items-center gap-2 pt-0.5">
            {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            <Switch data-testid="twofa-switch" checked={enabled} disabled={saving || pwPrompt} onCheckedChange={toggle} />
          </div>
        </div>

        {pwPrompt && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="twofa-disable-box">
            <p className="mb-2 text-sm font-semibold text-amber-800">Kapatmak için hesap şifrenizi girin</p>
            <Input type="password" data-testid="twofa-disable-password" value={pw} autoComplete="current-password"
              onChange={(e) => setPw(e.target.value)} placeholder="Hesap şifreniz" className="mb-3" />
            <div className="flex gap-2">
              <Button data-testid="twofa-disable-confirm" disabled={saving || !pw} onClick={() => doDisable(pw)}
                className="bg-red-600 text-white hover:bg-red-700">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} 2FA'yı Kapat
              </Button>
              <Button variant="outline" data-testid="twofa-disable-cancel" disabled={saving}
                onClick={() => { setPwPrompt(false); setPw(""); }}>Vazgeç</Button>
            </div>
          </div>
        )}

        <p className={`text-xs font-medium ${enabled ? "text-brand-green" : "text-slate-400"}`}>
          {enabled ? `● İki adımlı doğrulama aktif · ${activeMethodLabel}` : "○ İki adımlı doğrulama kapalı"}
        </p>
      </DialogContent>
    </Dialog>
  );
}
